import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,  
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Typography,
} from "@mui/material";
import { AddCircleOutlineOutlined, Close } from "@mui/icons-material";

import CustomTextField from "../../../components/ui/Textfield";
import apiService from "../../../service/Api";
import useDebounce from "../../../hooks/useDebounce";
import { RootState } from "../../../store/Index";
import { SearchPilotDialogProps, UserProps } from "../types/search-pilot";

export const SearchPilotDialog: React.FC<SearchPilotDialogProps> = ({
  searchMemberOpen,
  setSearchMemberOpen,
  searchResults,
  setSearchResults,
  setAddMemberOpen,
}) => {
  const [userOptions, setUserOptions] = useState<UserProps[]>([]);
  const [userVal, setUserVal] = useState("");
  const [usersLoading, setUsersLoading] = useState(false);

  const debouncedUserSearch = useDebounce(userVal, 400);

  const reduxTheme = useSelector((state: RootState) => state.theme);

  const closeAndReset = () => {
    setSearchMemberOpen(false);
    setUserVal("");
    setUserOptions([]);
  };  

  const fetchUsers = useCallback(async (userSearching: string) => {
    if (!userSearching) {
      setUserOptions([]);
      return;
    }

    setUsersLoading(true);
    try {
      const users = await apiService.get<{
        count: number;
        results: UserProps[];
        next: string | null;
        previous: string | null;
      }>("/api/authentication/users/", { search: userSearching });
      
      setUserOptions(users.data.results || []);
    } catch (err) {
      console.error("Failed to fetch roles:", err);
      setUserOptions([]);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers(debouncedUserSearch);
  }, [debouncedUserSearch, fetchUsers]);

  return (
    <Dialog
      open={searchMemberOpen}
      onClose={closeAndReset}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Search crew members
        <IconButton
          onClick={closeAndReset}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box mt={1}>
          <Autocomplete
            multiple={false}
            options={userOptions}
            getOptionLabel={(option) => {
              const user = option?.id ? `${option.id} - ${option.first_name} ${option.last_name}` : "No Users To Display";
              return user;
            }}
            value={searchResults}
            onChange={(_, newValue) => setSearchResults(newValue)}
            inputValue={userVal}
            onInputChange={(_, newInput) => setUserVal(newInput)}
            loading={usersLoading}
            renderInput={(params) => (
              <CustomTextField
                {...params}
                placeholder="Search users..."
                variant="outlined"
                helperText="Type 3 letters to search"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {usersLoading ? <CircularProgress size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const user = option?.id ? `${option.id} - ${option.first_name} ${option.last_name}` : "No Users To Display";
                return (
                  <Chip
                    label={user}
                    {...getTagProps({ index })}
                    key={option.id}
                    sx={{
                      backgroundColor: reduxTheme.primary_colors[800],
                      borderRadius: 1,
                      color: reduxTheme.primary_colors[100],
                    }}
                  />
                );
              })
            }
            sx={{ minWidth: 300 }}
          />
          <List
            sx={{
              backgroundColor: reduxTheme.primary_colors[800],
              borderRadius: 3,
              mt: 1,
              maxHeight: 300,
              overflowY: "auto",
              minHeight: 100,
            }}
          >
            {userVal.trim().length < 2 ? (
              <ListItem>
                <ListItemText
                  primary="Type at least 2 characters to search..."
                  sx={{ color: reduxTheme.primary_colors[100] }}
                />
              </ListItem>
            ) : usersLoading ? (
              <ListItem>
                <CircularProgress
                  size={20}
                  sx={{ mr: 2, color: reduxTheme.primary_colors[100] }}
                />
                <ListItemText
                  primary="Searching..."
                  sx={{ color: reduxTheme.primary_colors[100] }}
                />
              </ListItem>
            ) : !searchResults ? (
              <ListItem>
                <ListItemText
                  primary="No pilots selected."
                  sx={{ color: reduxTheme.primary_colors[100] }}
                />
              </ListItem>
            ) : (
               <>
                 <ListItem component="div">
                  <ListItemAvatar>
                    <Avatar src={searchResults?.profile_picture || "/placeholder.svg"} />
                  </ListItemAvatar>
                  <Box>
                    <Typography variant={'subtitle2'}>{`${searchResults?.first_name} ${searchResults?.last_name}`}</Typography>
                    <Typography variant={'caption'}>{searchResults?.user_type}</Typography>
                  </Box>
                </ListItem>
              </>
            )}
          </List>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={closeAndReset}
          sx={{
            color: reduxTheme.primary_colors[100],
            backgroundColor: reduxTheme.primary_colors[800],
          }}
        >
          Cancel
        </Button>
        <Button
          startIcon={<AddCircleOutlineOutlined />}
          sx={{
            color: reduxTheme.primary_colors[900],
            backgroundColor: reduxTheme.primary_colors[400],
          }}
          onClick={() => {
            closeAndReset();
            setAddMemberOpen(true);
          }}
        >
          Add new pilot
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SearchPilotDialog;
